import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, Mail, Clock, ShieldCheck, Users, Globe2, Award, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '', consent: false });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast({ title: 'Please provide consent', description: 'Tick the consent box so we can process your enquiry.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name: form.name, email: form.email, message: form.message });
      toast({ title: 'Message received', description: `Thanks ${form.name.split(' ')[0]}, our team at admin@waantravel.com will reply within 1 business day.` });
      setForm({ name: '', email: '', message: '', consent: false });
    } catch (err) {
      toast({ title: 'Could not send message', description: 'Please try again or email admin@waantravel.com directly.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-teal-800 py-14 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-teal-200 uppercase tracking-widest text-xs font-semibold">About us</div>
            <h1 className="font-display font-extrabold text-3xl md:text-5xl mt-2">A Singapore travel platform built for modern travellers</h1>
            <p className="text-white/85 mt-3">Cheapora is an online travel brand operated by Waan Travel Pte. Ltd. (UEN 202418948C), a licensed Singapore Travel Agent — STB Licence TA03795. We combine metasearch technology with old-fashioned Singapore hospitality.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Users, k: '120k+', v: 'Happy travellers' },
              { icon: Globe2, k: '180+', v: 'Destinations' },
              { icon: Award, k: '2 yrs', v: 'In business' },
              { icon: ShieldCheck, k: 'STB', v: 'Licensed TA03795' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-4">
                <s.icon className="w-5 h-5 text-amber-300" />
                <div className="font-display font-extrabold text-2xl mt-2">{s.k}</div>
                <div className="text-xs text-white/80">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-slate-900">Get in touch</h2>
          <p className="text-slate-600 mt-2">Have a question about a booking, or want to partner with Cheapora? Our team is based in Singapore and replies within 1 business day.</p>

          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><MapPin className="w-4 h-4" /></div>
              <div>
                <div className="font-display font-bold text-slate-900">Head office</div>
                <div className="text-sm text-slate-600">1 Soon Lee Street, #03-37 Pioneer Centre,<br/>Singapore 627605</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><Mail className="w-4 h-4" /></div>
              <div>
                <div className="font-display font-bold text-slate-900">Email</div>
                <div className="text-sm text-slate-600">admin@waantravel.com</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div>
              <div>
                <div className="font-display font-bold text-slate-900">Company registration</div>
                <div className="text-sm text-slate-600">Cheapora is operated by Waan Travel Pte. Ltd.<br/>UEN 202418948C · Licensed Singapore Travel Agent — STB Licence TA03795<br/>Website: www.cheapora.com</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><Clock className="w-4 h-4" /></div>
              <div>
                <div className="font-display font-bold text-slate-900">Office hours</div>
                <div className="text-sm text-slate-600">Mon–Fri 9am–6pm · Sat 10am–2pm (SGT)</div>
              </div>
            </div>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="font-display font-bold text-lg text-slate-900 mb-4">Send us a message</div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Jane Tan" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="jane@email.com" />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tell us how we can help…" />
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({...form, consent: !!v})} />
                <span>I consent to Waan Travel Pte. Ltd. processing my contact details to respond to my enquiry, in accordance with the <a href="/privacy" className="text-teal-700 underline">Privacy Policy</a>.</span>
              </label>
              <Button type="submit" disabled={loading} className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…</> : 'Send message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
